/**
 * FRONTEND LABELGRID WRAPPER — deployment/runtime blockers around the shared contract.
 *
 * The provider wire format lives in `src/contracts/labelgrid.ts`, which both the
 * frontend and the server may import safely. This file is the frontend-only layer that
 * answers a different question: given this build's runtime configuration, can the app
 * actually reach a backend that could talk to LabelGrid?
 */
import { API_BASE_URL } from '../config';
import {
  LABELGRID_BASE_URL,
  type LabelGridContentType,
} from '../contracts/labelgrid';

export * from '../contracts/labelgrid';

// ---------------------------------------------------------------------------
// What stands in the way
// ---------------------------------------------------------------------------

/**
 * One reason a LabelGrid request cannot be issued from this application.
 *
 * The same record shape as LyriaBlocker, deliberately: two integrations, one honest
 * pattern, and a UI that renders both the same way.
 */
export interface LabelGridBlocker {
  id: 'no-backend' | 'no-credentials' | 'no-account-resources' | 'no-audio-asset';
  /** One line, in the words the user or the reviewer needs. */
  summary: string;
  /** What would have to change. */
  resolution: string;
}

/**
 * Everything that has to be true before a release can actually be distributed, and is
 * not true in this build.
 *
 * FOUR BLOCKERS, AND NONE OF THEM IS A TODO THAT COULD BE SHORTCUT:
 *
 *   1. NO BACKEND. Computed, so the entry disappears on its own the day
 *      `VITE_API_BASE_URL` is set — at which point publisherApi's `assertMock` starts
 *      failing loudly until the real requests are implemented.
 *
 *   2. NO CREDENTIALS, AND THEY CANNOT LIVE HERE. LabelGrid authenticates with a
 *      single bearer token carrying full account authority. A token given to a Vite
 *      build is inlined into the bundle and served to every visitor, so
 *      "add VITE_LABELGRID_TOKEN" is not a smaller version of "add a backend" — it is
 *      publishing the key to the whole internet, and it would let anyone distribute
 *      releases, issue takedowns and read royalty statements on the account. There is
 *      deliberately no client-side token path in src/config.ts and adding one must be
 *      treated as a security regression, not a convenience.
 *
 *   3. NO ACCOUNT RESOURCES. `label_id`, `artist_id` and `primary_genre_id` are
 *      required foreign keys into the LabelGrid account. They are not derivable from
 *      anything in this repository.
 *
 *   4. NO AUDIO ASSET. This one is not about LabelGrid at all, and it is the blocker a
 *      reader is most likely to be surprised by: `Song.audioUrl` is null for every
 *      song in Lyrixa, because generation is simulated (see api/generationApi.ts and
 *      stores/playerStore.ts). A distribution platform's one non-negotiable input is
 *      the audio master, uploaded through
 *      `POST /tracks/{track}/files/stereo/upload-url`. Until Lyria 3 is actually
 *      reachable and returns real audio, there is no file to deliver — so no release
 *      could succeed even with a fully configured backend and valid credentials.
 *
 * Always non-empty in this build, which is why `canDistribute()` is always false.
 */
export function describeLabelGridBlockers(): LabelGridBlocker[] {
  const blockers: LabelGridBlocker[] = [];

  if (!API_BASE_URL) {
    blockers.push({
      id: 'no-backend',
      summary:
        'No Lyrixsa backend is configured, so there is nothing to send a distribution request to.',
      resolution:
        'Set VITE_API_BASE_URL and implement the Publisher endpoints server-side, forwarding to ' +
        `${LABELGRID_BASE_URL}. See docs/publisher-backend.md.`,
    });
  }

  blockers.push({
    id: 'no-credentials',
    summary:
      'LabelGrid requires a bearer token with full account authority, and the browser cannot ' +
      'hold one.',
    resolution:
      'Keep the token server-side. A token given to a Vite build is inlined into the bundle and ' +
      'shipped to every visitor, which would let anyone distribute releases, issue takedowns ' +
      'and read royalty statements on the account.',
  });

  blockers.push({
    id: 'no-account-resources',
    summary:
      'A release requires a LabelGrid label_id, artist_id and primary_genre_id, which only ' +
      'exist inside a configured LabelGrid account.',
    resolution:
      'Create the label and artist records through /labels and /artists, and read the genre ' +
      'catalogue from /genres, using real credentials.',
  });

  blockers.push({
    id: 'no-audio-asset',
    summary:
      'Lyrixa songs carry no audio file — generation is simulated, so Song.audioUrl is null — ' +
      'and a distributor cannot accept a release without an audio master.',
    resolution:
      'Make generation real (see api/lyria.ts, which is blocked on the same backend) so a ' +
      'track has a file to upload through POST /tracks/{track}/files/stereo/upload-url.',
  });

  return blockers;
}

/** True when a release could actually be distributed. False in this build. */
export function canDistribute(_contentType?: LabelGridContentType): boolean {
  return describeLabelGridBlockers().length === 0;
}
